// Daytime ambience. Two real recordings from Sam's garden (Highfield 31 and
// 32, half a minute each, transcoded to mono ogg) rotate forever with a
// crossfade between them, so there is no seam and no spike. The old bed was
// a 2.9 second whistle looping for eternity, which is exactly as maddening
// as it sounds; it survives as an occasional accent over the top rather
// than the floor of the world.
//
// The master gain follows daylight, same contract as before: full at noon,
// quiet at night, ramped rather than stepped.

import { getCtx, loadBuffer } from './context'

const BEDS = [
  ['/sfx/birds/highfield31.ogg'],
  ['/sfx/birds/highfield32.ogg'],
]
const WHISTLE = ['/sfx/birds/birdSong.ogg', '/sfx/birds/birdSong.wav']

// Ceiling for the bed at full daylight. Ambience should sit under everything.
// Up 40% from 0.22: the garden recordings are quiet source material and
// sat under the world rather than in it. The whistle rides this same
// master, so WHISTLE_LEVEL below is divided by the same 1.4 to leave the
// accent exactly where it was; only the beds get louder.
export const BIRDSONG_MAX_GAIN = 0.308

// Overlap between one bed ending and the next beginning.
const XFADE_S = 3

// The whistle drops in somewhere between these, at a fraction of the bed.
const WHISTLE_MIN_S = 50
const WHISTLE_MAX_S = 140
// 0.3 / 1.4. Effective level against the raised master is unchanged at
// 0.066, which is where it was when Sam said the beds were too quiet and
// the whistle was fine.
const WHISTLE_LEVEL = 0.214

let master = null
let running = false
let starting = false
let bedIndex = 0
let nextTimer = null
let whistleTimer = null

function playBed(c, buffer) {
  const g = c.createGain()
  g.gain.setValueAtTime(0, c.currentTime)
  g.gain.linearRampToValueAtTime(1, c.currentTime + XFADE_S)
  g.connect(master)
  const s = c.createBufferSource()
  s.buffer = buffer
  s.connect(g)
  s.start(0)
  // Fade this bed out over the last XFADE_S of its life.
  const fadeAt = c.currentTime + buffer.duration - XFADE_S
  g.gain.setValueAtTime(1, fadeAt)
  g.gain.linearRampToValueAtTime(0, fadeAt + XFADE_S)
  s.stop(c.currentTime + buffer.duration + 0.1)
}

async function rotate(c, buffers) {
  if (!running) return
  const buffer = buffers[bedIndex % buffers.length]
  bedIndex++
  playBed(c, buffer)
  // The next bed starts as this one begins to fade, which is the crossfade.
  nextTimer = setTimeout(() => rotate(c, buffers), (buffer.duration - XFADE_S) * 1000)
}

function scheduleWhistle(c, buffer) {
  if (!running || !buffer) return
  const wait = (WHISTLE_MIN_S + Math.random() * (WHISTLE_MAX_S - WHISTLE_MIN_S)) * 1000
  whistleTimer = setTimeout(() => {
    try {
      const g = c.createGain()
      g.gain.value = WHISTLE_LEVEL
      g.connect(master)
      const s = c.createBufferSource()
      s.buffer = buffer
      s.connect(g)
      s.start(0)
    } catch { /* an accent is never worth an error */ }
    scheduleWhistle(c, buffer)
  }, wait)
}

/**
 * Start the ambience once the context is running. Idempotent: safe to call
 * every frame. Starts silent; setBirdsongDaylight drives the level.
 */
export function ensureBirdsong() {
  if (running || starting) return
  const c = getCtx()
  if (!c || c.state !== 'running') return
  starting = true
  Promise.all([loadBuffer(BEDS[0]), loadBuffer(BEDS[1]), loadBuffer(WHISTLE)])
    .then(([a, b, whistle]) => {
      if (running) return
      const beds = [a, b].filter(Boolean)
      if (beds.length === 0) return
      master = c.createGain()
      master.gain.value = 0
      master.connect(c.destination)
      running = true
      bedIndex = Math.random() < 0.5 ? 0 : 1
      rotate(c, beds)
      scheduleWhistle(c, whistle)
    })
    .catch(() => {})
    .finally(() => { starting = false })
}

/**
 * daylight 0..1 (1 = full day). Ramped rather than set so dusk and dawn fade
 * instead of stepping.
 */
export function setBirdsongDaylight(daylight) {
  if (!master) return
  const c = getCtx()
  if (!c) return
  const target = Math.max(0, Math.min(1, daylight)) * BIRDSONG_MAX_GAIN
  try {
    master.gain.cancelScheduledValues(c.currentTime)
    master.gain.setTargetAtTime(target, c.currentTime, 1.5)
  } catch { /* fine */ }
}

/** Stop everything. For teardown paths that used to null the old source. */
export function stopBirdsong() {
  running = false
  if (nextTimer) clearTimeout(nextTimer)
  if (whistleTimer) clearTimeout(whistleTimer)
  nextTimer = whistleTimer = null
  try { master?.disconnect() } catch { /* fine */ }
  master = null
}
